import { useQuery } from '@tanstack/react-query';
import { LogOut, Mail, User, Shield, ExternalLink, Activity } from 'lucide-react';
import { useSession } from '../contexts/SessionContext';
import { api } from '../api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';

export function SettingsScreen() {
  const { user, logout } = useSession();
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: api.listProjects,
  });
  const { data: domainsData } = useQuery({
    queryKey: ['allDomains'],
    queryFn: () => api.allDomains(),
  });

  const projectCount = projectsData?.projects.length ?? 0;
  const domainCount = domainsData?.domains.length ?? 0;
  const liveCount =
    projectsData?.projects.filter((p) => !!p.liveDeploymentId).length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight2 text-zinc-900">Settings</h1>
        <p className="text-[13.5px] text-zinc-500 mt-1">Account, workspace and quick links.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-4 w-4 text-zinc-400" />
              Account
            </CardTitle>
            <CardDescription>
              Signed in via session. Passwords reset by the administrator.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field
              label="Display name"
              value={user?.displayName ?? '—'}
              icon={<User className="h-3.5 w-3.5" />}
            />
            <Field
              label="Email"
              value={user?.email ?? '—'}
              icon={<Mail className="h-3.5 w-3.5" />}
            />
            <Field
              label="User ID"
              value={user?.id ?? '—'}
              icon={<Shield className="h-3.5 w-3.5" />}
              mono
            />
            <div className="pt-2 border-t border-zinc-100">
              <Button
                variant="outline"
                onClick={() => void logout()}
                leftIcon={<LogOut className="h-3.5 w-3.5" />}
              >
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-zinc-400" />
              Workspace
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Stat label="Total sites" value={projectCount} />
            <Stat label="Live sites" value={liveCount} tone="green" />
            <Stat label="Domains owned" value={domainCount} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick links</CardTitle>
          <CardDescription>Useful external dashboards.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-zinc-100 -my-2">
            <QuickLink
              href="https://dash.cloudflare.com"
              label="Cloudflare Dashboard"
              hint="Pages projects, DNS, custom domains"
            />
            <QuickLink
              href="https://search.google.com/search-console"
              label="Google Search Console"
              hint="Indexing status + sitemap submissions"
            />
            <QuickLink
              href="https://analytics.google.com"
              label="Google Analytics 4"
              hint="Traffic + lead conversions"
            />
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
        </CardHeader>
        <CardContent className="text-[13px] text-zinc-600 space-y-1.5 leading-relaxed">
          <p>
            Sitekit Factory generates multi-page SEO websites from a single
            <code className="mx-1 font-mono text-[11.5px] bg-zinc-100 px-1 rounded">brand.json</code>
            file, deploys to Cloudflare Pages, and pings Google + IndexNow for
            instant indexing.
          </p>
          <p className="text-zinc-500 text-[12px]">
            Engine: <Badge tone="neutral" className="ml-1">connected</Badge>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  icon,
  mono,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-[11.5px] uppercase tracking-wide text-zinc-400 mb-1">{label}</div>
      <div className="flex items-center gap-2 text-[14px] text-zinc-900">
        {icon && <span className="text-zinc-400">{icon}</span>}
        <span className={mono ? 'font-mono text-[12.5px]' : ''}>{value}</span>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'green' }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[13px] text-zinc-600">{label}</span>
      <span
        className={
          'tabular-nums text-[15px] font-semibold ' +
          (tone === 'green' ? 'text-emerald-700' : 'text-zinc-900')
        }
      >
        {value}
      </span>
    </div>
  );
}

function QuickLink({ href, label, hint }: { href: string; label: string; hint: string }) {
  return (
    <li className="py-2">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-between gap-3 px-2 py-2 rounded-md hover:bg-zinc-50 transition-colors group"
      >
        <div>
          <div className="text-[13.5px] font-medium text-zinc-900">{label}</div>
          <div className="text-[12px] text-zinc-500">{hint}</div>
        </div>
        <ExternalLink className="h-3.5 w-3.5 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
      </a>
    </li>
  );
}
